import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Droplet, Home as HomeIcon, Settings, Shield, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import { Btn, Eyebrow, HeroImage, MaskTitle, Reveal } from "@/components/site/Reveal";
import FeatureStrip from "@/components/site/FeatureStrip";
import Process from "@/components/site/Process";
import BeforeAfter from "@/components/site/BeforeAfter";
import { scrollToId } from "@/lib/scroll";

export const CORE_FEATURES = [
  { icon: Droplet, title: "Cleaner, healthier water", text: "High end purification equipment for your home." },
  { icon: HomeIcon, title: "Door to door service", text: "We come to you — right here in San Diego." },
  { icon: Settings, title: "Professional installation", text: "Set up and support from our team." },
  { icon: Shield, title: "A healthier home", text: "Great-tasting water for you and your family." },
];

const CARDS = [
  { id: "whole-home", img: "/img/tank.jpg", title: ["Whole Home", "Water Filtration"], text: "Cleaner water throughout your entire home." },
  { id: "cartridge", img: "/img/cartridge.jpg", title: ["Catalytic", "Cartridge"], text: "Keep your system performing at its best." },
];

const ProductCard = ({ c, i }) => {
  const navigate = useNavigate();
  return (
    <Reveal delay={0.1 + i * 0.12}>
      <button data-testid={`home-product-card-${c.id}`} onClick={() => navigate("/products")} className="group block w-full text-left">
        <div className="relative aspect-[4/5] overflow-hidden bg-[#EBEBEB]">
          <img src={c.img} alt={c.title.join(" ")} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        </div>
        <div className="mt-5 flex items-end justify-between gap-4">
          <div>
            <h3 className="font-head font-extrabold uppercase text-[17px] leading-tight">{c.title[0]}<br />{c.title[1]}</h3>
            <p className="mt-2 text-[15px] text-[var(--body)] leading-snug">{c.text}</p>
          </div>
          <ChevronRight className="h-5 w-5 shrink-0 mb-1 transition-transform duration-300 group-hover:translate-x-1" />
        </div>
      </button>
    </Reveal>
  );
};

export default function Home() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return undefined;
    const t = setTimeout(() => scrollToId(hash.slice(1)), 350);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <>
      <section data-testid="home-hero" className="grid lg:grid-cols-2 min-h-[600px] border-b border-[var(--line)]">
        <div className="flex flex-col justify-center px-5 sm:px-8 lg:pl-12 lg:pr-10 xl:pl-[max(3rem,calc((100vw-1280px)/2+3rem))] py-14 lg:py-20">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="eyebrow">Cleaner water. Healthier homes.</motion.p>
          <MaskTitle testId="home-hero-title" lines={["Premium water", "purification", "for your home."]} className="mt-5 text-[2.6rem] sm:text-6xl xl:text-[4.25rem]" />
          <Reveal delay={0.5}>
            <p className="mt-6 text-[17px] sm:text-lg text-[var(--body)] leading-relaxed max-w-md">Water System Bros. brings high end water purification equipment directly to San Diego homeowners. Cleaner, better-tasting water for a healthier lifestyle.</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Btn to="/signup" testId="home-hero-signup-btn">Sign Up</Btn>
              <Btn to="/products" variant="outline" chevron={false} testId="home-hero-products-btn">Explore Products</Btn>
            </div>
          </Reveal>
        </div>
        <HeroImage testId="home-hero-image" src="/img/kitchen.jpg" alt="Black kitchen faucet in a bright modern kitchen" className="min-h-[360px] lg:min-h-full" />
      </section>

      <FeatureStrip items={CORE_FEATURES} testId="home-feature-strip" />

      <section data-testid="home-products-section" className="bg-[var(--soft)] py-20 lg:py-24">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5">
            <Eyebrow>Our Products</Eyebrow>
            <h2 data-testid="home-products-title" className="headline mt-4 text-4xl sm:text-5xl">Water solutions<br />for every home.</h2>
            <span className="block mt-5 h-[2px] w-6 bg-[var(--ink)]" />
            <p className="mt-5 text-[17px] text-[var(--body)] leading-relaxed max-w-md">Our whole home filtration system gives your family cleaner, better-tasting water from every tap — installed and supported by our team.</p>
            <Btn to="/products" testId="home-explore-products-btn" className="mt-8">Explore Products</Btn>
          </Reveal>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {CARDS.map((c, i) => <ProductCard key={c.id} c={c} i={i} />)}
          </div>
        </div>
      </section>

      <section id="why" data-testid="home-why-section" className="grid lg:grid-cols-2 bg-white">
        <Reveal y={0} className="relative min-h-[320px] lg:min-h-[440px] overflow-hidden">
          <img src="/img/house.jpg" alt="Modern white home with palm tree" className="absolute inset-0 h-full w-full object-cover" />
        </Reveal>
        <div className="flex flex-col justify-center px-5 sm:px-8 lg:px-14 py-14 lg:py-16">
          <Reveal>
            <Eyebrow>Why Water System Bros.</Eyebrow>
            <h2 data-testid="home-why-title" className="headline mt-4 text-4xl sm:text-5xl">A cleaner,<br />brighter San Diego.</h2>
            <p className="mt-5 text-[17px] text-[var(--body)] leading-relaxed max-w-lg">We're a group of bros who wanted to work and hustle and provide purified water to the people of San Diego. Our mission is simple — help homeowners get access to high quality water with professional service and premium equipment.</p>
            <Btn to="/signup" testId="home-why-signup-btn" className="mt-8">Sign Up Today</Btn>
          </Reveal>
        </div>
      </section>

      <Process />
      <BeforeAfter />
    </>
  );
}
